<template>
  <section ref="rootEl" class="container-card" @click="handleCardClick(props.order)">
    <Transition name="fade-slide" mode="out-in" appear>
      <div :class="['card-wrapper', { 'active-card border-none': isActiveCard }]">
        <div class="trip-card-header d-flex justify-content-between align-items-center">
          <div class="trip-title d-flex gap-1">
            <p class="m-0 fw-bold">{{ order.name }}</p>
          </div>
          <div class="package-info d-flex align-items-center">
            <span class="package-icon me-1">📦</span>{{ order.orderCount }} Pesanan
          </div>
        </div>

        <div class="body">
          <div class="trip-details d-flex justify-content-between">
            <div class="detail-item">
              <span class="label">Order ID GoSend</span>
              <span class="value id-gosend text-green">{{ order.orderNo }}</span>
            </div>
            <div class="detail-item text-end">
              <span class="label">Nomor Shipment</span>
              <span class="value no-s text-blue">{{ order.shipmentNo }}</span>
            </div>
          </div>
          <div class="line-br"></div>
        </div>

        <div class="footer">
          <div class="driver-info">
            <img
              :src="order.courierImageUrl || user"
              @error="handleImageError"
              alt="driver"
              class="avatar border"
            />
            <div class="driver-details">
              <p class="name">{{ order.courierName }}</p>
              <p v-if="order.originalStatus !== 'confirmed'" class="plate">{{ order.plateNumber }}</p>
            </div>
          </div>
          <div class="btn-ready" :style="getStatusClass(order.tripStatus)">
            {{ getStatusLabel(order.tripStatus) }}
          </div>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
import { useThreeeplStore } from "~/composables/stores/useThreeplStore";
import { ColorPicker } from "~~/constant/color.constat";
import user from "~/assets/images/user.svg?url"
import type { ITrip } from "~~/types/liveTracking/3pl.interface";

const { detailTrip } = useThreeeplStore();

const props = defineProps<{
  order: ITrip;
}>();

const emit = defineEmits<{
  (e: "selectTrip", trip: ITrip): void;
}>();

const handleCardClick = (trip: ITrip) => {
  emit("selectTrip", trip);
};

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  target.src = user; 
};

const getStatusLabel = (status: number) => {
  switch (status) {
    case 1206:
      return "Siap Kirim";
    case 1207:
      return "Dikirim";
    case 1204:
      return "Selesai";
    default:
      return "-";
  }
};

const getStatusClass = (status: number) => {
  let bgColor = "";
  let textColor = "";
  let borderColor = "";

  switch (status) {
    case 1206:
      bgColor = 'white';
      textColor = ColorPicker.blueLight;
      borderColor = ColorPicker.blueLight;
      break;
    case 1207:
      bgColor = ColorPicker.purpleLight;
      textColor = ColorPicker.purpleDark;
      borderColor = ColorPicker.purpleDark;
      break;
    case 1204:
      bgColor = ColorPicker.toscaLight;
      textColor = ColorPicker.toscaDark;
      borderColor = ColorPicker.toscaDark;
      break;
  }
  return {
    backgroundColor: bgColor,
    color: textColor,
    borderColor: borderColor,
  };
};

const isActiveCard = computed(() => {
  return detailTrip.value?.id === props.order.id;
});
</script>

<style lang="scss" scoped>
$color-card-bg: #fff;
$color-header-bg: #ffeef6;
$color-border-light: #eee;
$color-text-dark: #333;
$color-text-secondary: #777;
$color-orange: #ff8c00;
$color-active-green: #00a993;
$color-inactive-gray: #e0e0e0;
$color-button-delivered-bg: #ecedff;
$color-button-delivered-text: #6a0dad;

.text-green {
  color: #10a94b !important;
}

.text-blue {
  color: #005da6 !important;
}

.card-wrapper {
  border: 2px solid $color-border-light;
  border-radius: 15px;
  font-family: sans-serif;
  background-color: $color-header-bg;
  overflow: hidden;
  cursor: pointer;

  &.active-card {
    border: 2px solid #d00b0b;
    transition: ease-in 0.3s;
  }

  border: 2px solid $color-border-light;
  border-radius: 15px;
  font-family: sans-serif;
  background-color: $color-header-bg;
  overflow: hidden;
  cursor: pointer;

  .trip-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    border-bottom: 1px solid $color-border-light;
  }

  .body {
    border-radius: 20px 20px 0 0;
    background-color: white;
    padding: 15px;
    .line-br {
      height: 1px;
      width: 100%;
      background-color: #d8d8d8;
    }
    .trip-details {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      gap: 10px;

      .detail-item {
        font-size: 14px;
        display: flex;
        flex-direction: column;
        .label {
          color: $color-text-secondary;
          margin-bottom: 5px;
        }

        .value {
          font-weight: bold;
          color: $color-text-dark;

          .rounded-titik {
            border-radius: 100%;
            width: 5px;
            height: 5px;
            background-color: #999999;
          }
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: end;
    background-color: white;
    padding: 0 15px 15px 15px;

    .driver-info {
      display: flex;
      align-items: center;
      gap: 10px;

      .avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        padding: 2px;
      }
      .name {
        margin: 0;
        font-weight: bold;
        font-size: 14px;
        color: #333;
      }
      .plate {
        margin: 0;
        font-size: 12px;
        color: #666;
      }
    }

    .btn-ready {
      border: 1.5px solid;
      background: transparent;
      border-radius: 8px;
      padding: 3px 12px;
      font-size: 13px;
      cursor: pointer;
      &:hover {
        background: #f0faff;
      }
    }
  }
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px); 
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px); 
}
</style>
